import React from 'react';

import { gql, useMutation } from '@apollo/client';

import type { FetchResult, MutationHookOptions } from '@apollo/client';
import type { JobHiringAttendance } from '../type';

type UpdatedAttendance = Pick<
  JobHiringAttendance,
  'id' | 'status' | 'startOperationTime' | 'endOperationTime' | 'breakDurationMinute'
>;

type UpdateAttendancesData = {
  updateJobHiringAttendances: {
    success: boolean;
    errors?: string[][] | null;
    data?: UpdatedAttendance[] | null;
  };
};

export type UpdateAttendanceInput = {
  id: string;
  startOperationTime?: string | null;
  endOperationTime?: string | null;
  breakDurationMinute?: number | null;
};

type UpdateAttendancesVariables = { attendances: UpdateAttendanceInput[] };

export type UpdateAttendancesResult = {
  success: boolean;
  data?: UpdatedAttendance[] | null;
  errors?: string[][] | null;
  error?: Error | unknown | null;
  result?: FetchResult<UpdateAttendancesData> | null;
};

const updateAttendancesGql = gql`
  mutation updateJobHiringAttendances($attendances: [AttendanceInput!]!) {
    updateJobHiringAttendances(attendances: $attendances) {
      success
      errors
      data {
        id
        status
        startOperationTime
        endOperationTime
        breakDurationMinute
      }
    }
  }
`;

const useUpdateAttendancesMutation = (
  options?: MutationHookOptions<UpdateAttendancesData, UpdateAttendancesVariables>,
) => {
  const [updateAttendancesMutation, mutationResult] = useMutation<UpdateAttendancesData, UpdateAttendancesVariables>(
    updateAttendancesGql,
    options,
  );

  const updateAttendances = React.useCallback(
    async (attendances: UpdateAttendanceInput[]): Promise<UpdateAttendancesResult> => {
      try {
        const result = await updateAttendancesMutation({ variables: { attendances } });

        return {
          success: result.data?.updateJobHiringAttendances.success ?? false,
          data: result.data?.updateJobHiringAttendances.data ?? null,
          errors: result.data?.updateJobHiringAttendances.errors ?? null,
          result,
        };
      } catch (error) {
        return {
          success: false,
          error,
        };
      }
    },
    [updateAttendancesMutation],
  );

  return {
    updateAttendances,
    mutate: updateAttendancesMutation,
    result: mutationResult,
  };
};

export default useUpdateAttendancesMutation;
